import { useState } from "react";
import { Link } from "react-router-dom";
import { Search as SIcon } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { api, errMsg } from "../lib/api";
import { Badge, Btn, Card, Field, Input, Select, PageHeader, Money, Th, Td, Empty, useNames } from "../components/common";
import ScopePicker from "../components/ScopePicker";

export default function SearchPage() {
  const { meta, user } = useAuth();
  const { gName, uName } = useNames(meta);
  const [f, setF] = useState({ q: "", type: "all", gkk_id: "", unit_id: "", status: "", date_from: "", date_to: "", min_amount: "", max_amount: "", category_id: "" });
  const [res, setRes] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const go = async (e) => {
    e?.preventDefault();
    try { const { data } = await api.get("/search", { params: Object.fromEntries(Object.entries(f).filter(([, v]) => v)) }); setRes(data); } catch (x) { toast.error(errMsg(x)); }
  };
  if (user.role === "member") return <Empty>Members can view records through the financial report.</Empty>;
  return (
    <div>
      <PageHeader title="Search Records" subtitle="Find collections, expenses and remittances you are authorized to see." />
      <Card className="mb-6">
        <form onSubmit={go} className="space-y-4">
          <Field label="Reference no., treasurer, venue, occasion, special collection, payee or recipient"><Input data-testid="search-query-input" value={f.q} onChange={set("q")} placeholder="e.g. COL-2026, World Mission, Unit 1 Chapel" /></Field>
          <ScopePicker gkk={f.gkk_id} unit={f.unit_id} allowAll onChange={(g, u) => setF({ ...f, gkk_id: g, unit_id: u })} testid="search" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Field label="Record type"><Select data-testid="search-type-select" value={f.type} onChange={set("type")}><option value="all">All</option><option value="collection">Collections</option><option value="expense">Expenses</option><option value="remittance">Remittances</option></Select></Field>
            <Field label="Status"><Select data-testid="search-status-select" value={f.status} onChange={set("status")}><option value="">Any</option>{["submitted", "pending_verification", "verified", "finalized", "rejected", "approved", "paid", "draft"].map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}</Select></Field>
            <Field label="Category"><Select value={f.category_id} onChange={set("category_id")}><option value="">Any</option>{meta.categories.filter((c) => c.kind !== "occasion").map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</Select></Field>
            <Field label="From"><Input type="date" value={f.date_from} onChange={set("date_from")} /></Field>
            <Field label="To"><Input type="date" value={f.date_to} onChange={set("date_to")} /></Field>
            <Field label="Min amount ₱"><Input inputMode="decimal" value={f.min_amount} onChange={set("min_amount")} /></Field>
            <Field label="Max amount ₱"><Input inputMode="decimal" value={f.max_amount} onChange={set("max_amount")} /></Field>
          </div>
          <Btn type="submit" data-testid="search-submit-button"><SIcon className="h-5 w-5" /> Search</Btn>
        </form>
      </Card>
      {res && (res.length === 0 ? <Empty>No matching records.</Empty> : (
        <div className="tbl-wrap">
          <table className="tbl" data-testid="search-results-table">
            <thead><tr><Th>Type</Th><Th>Reference</Th><Th>Date</Th><Th>Description</Th><Th>GKK / Unit</Th><Th>Status</Th><Th r>Amount</Th></tr></thead>
            <tbody>{res.map((x) => (
              <tr key={x.type + x.id}>
                <Td className="capitalize">{x.type}</Td>
                <Td>{x.type === "collection" ? <Link className="font-mono font-bold text-[#C85A32] hover:underline" to={`/collections/${x.id}`}>{x.ref_no}</Link> : <Link className="font-mono font-bold text-[#C85A32]" to={x.type === "expense" ? "/expenses" : "/remittances"}>{x.ref_no}</Link>}</Td>
                <Td>{x.date}</Td><Td>{x.title}<div className="text-xs text-[#6E6259]">{x.subtitle}</div></Td><Td>{gName(x.gkk_id)}<div className="text-xs text-[#6E6259]">{uName(x.unit_id)}</div></Td>
                <Td><Badge s={x.status} /></Td><Td r><Money c={x.amount_cents} /></Td>
              </tr>))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}
